import {
  InboxOutlined,
  DropboxOutlined,
  CarOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import { Card, Col, Row, Statistic } from 'antd'
import { useNavigate } from 'react-router'

import { useFoDisplayStatusConfig } from '../orders/foDisplayStatusConfig'
import { DISPLAY_STATUSES } from '../orders/orderDisplayStatuses'

type OrderCounts = {
  paymentPending: number
  paid: number
  shipped: number
  delivered: number
  confirmed: number
  allCancelled: number
}

type Props = {
  orderCounts: OrderCounts
  isLoading: boolean
}

type DashboardStatus = (typeof DISPLAY_STATUSES)[number]

/**
 * Count and icon per status. The label and the order come from the order list itself
 * (useFoDisplayStatusConfig, DISPLAY_STATUSES): the card that opened "결제 미완료" orders used to
 * be labelled "배송준비", so the dashboard and the list it links to disagreed.
 */
const CARD_BY_STATUS: Partial<
  Record<DashboardStatus, { countKey: keyof OrderCounts; icon: React.JSX.Element }>
> = {
  PAYMENT_PENDING: { countKey: 'paymentPending', icon: <DropboxOutlined /> },
  PAID: { countKey: 'paid', icon: <InboxOutlined /> },
  SHIPPED: { countKey: 'shipped', icon: <CarOutlined /> },
  DELIVERED: { countKey: 'delivered', icon: <CheckCircleOutlined /> },
  CONFIRMED: { countKey: 'confirmed', icon: <SafetyCertificateOutlined /> },
  ALL_CANCELLED: { countKey: 'allCancelled', icon: <CloseCircleOutlined /> },
}

export function DashboardOrderStatusCards({ orderCounts, isLoading }: Props): React.JSX.Element {
  const navigate = useNavigate()
  const statusConfig = useFoDisplayStatusConfig()

  return (
    <Row gutter={[16, 16]}>
      {DISPLAY_STATUSES.map((displayStatus) => {
        const card = CARD_BY_STATUS[displayStatus]
        if (!card) return null
        return (
          <Col key={displayStatus} xs={12} sm={8} md={4}>
            <Card
              data-testid={`dashboard-order-card-${displayStatus.toLowerCase().replace(/_/g, '-')}`}
              hoverable
              onClick={() => void navigate(`/orders?displayStatus=${displayStatus}`)}
            >
              <Statistic
                title={statusConfig[displayStatus].label}
                value={orderCounts[card.countKey]}
                prefix={card.icon}
                loading={isLoading}
              />
            </Card>
          </Col>
        )
      })}
    </Row>
  )
}
